<script setup lang="ts">
	import { useHotelsStore } from '@/stores/hotels'
	import { useReservationStore } from '@/stores/reservation'
	import {
		MinusIcon,
		PlusIcon,
		QuestionMarkCircleIcon,
	} from '@heroicons/vue/20/solid'
	import useVuelidate from '@vuelidate/core'
	import { storeToRefs } from 'pinia'
	import { computed, nextTick, ref, watch } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import AlertComponent from '@/components/AlertComponent.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import SearchRadiusInput from '@/components/SearchRadiusInput.vue'
	import StyledAlert from '@/components/StyledAlert.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledComboboxGroup from '@/components/StyledComboboxGroup.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledPoliciesModal from '@/components/StyledPoliciesModal.vue'
	import LoadingSpin from '@/components/icons/LoadingSpin.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import { useInlineFloatingRail } from '@/composables/useInlineFloatingRail'

	import type { Suggestion } from '@/interfaces/mapboxSearchSuggestResponse'

	import useCompanyUser from '@/modules/companies/composables/useCompanyUser'
	import TravelersCustomLabel from '@/modules/hotels/components/TravelersCustomLabel.vue'
	import useAvailabilityForm from '@/modules/hotels/composables/useAvailabilityForm'
	import useHotelBranches, {
		type BranchSuggestion,
		fromDefaultLocation,
	} from '@/modules/hotels/composables/useHotelBranches'
	import UnlinkItineraryWarningModal from '@/modules/trips/components/UnlinkItineraryWarningModal.vue'
	import { Request } from '@/modules/trips/interfaces/cef'
	import type { FlightRequestDetail } from '@/modules/trips/interfaces/tripDetail'
	import UserForm from '@/modules/users/components/UserForm.vue'
	import useUser from '@/modules/users/composables/useUser'

	import type { RecentHotelSearch } from '../interfaces/recentHotelSearch'
	import LinkedFlightBanner from './LinkedFlightBanner.vue'
	import PolicyErrors from './PolicyErrors.vue'

	interface Emits {
		(event: 'change', value: boolean): void
	}

	interface Props {
		expandHotelForm: boolean
		linkedFlight?: FlightRequestDetail | null
		linkedCefRequests?: Request[]
	}

	const props = defineProps<Props>()

	const emits = defineEmits<Emits>()

	const router = useRouter()
	const localDestCity = ref<string | null>(null)

	const showUnlinkAlert = ref(false)

	const handleUnlinkAlert = () => {
		showUnlinkAlert.value = true
	}

	const collapseOverflowVisible = ref(true)
	let expandTimeout: ReturnType<typeof setTimeout> | null = null

	const destinationCity = computed(
		() =>
			props.linkedFlight?.destinationAirport?.cityName ??
			props.linkedFlight?.destination ??
			null
	)

	watch(
		() => props.expandHotelForm,
		(isExpanded) => {
			if (expandTimeout) {
				clearTimeout(expandTimeout)
				expandTimeout = null
			}

			if (isExpanded) {
				expandTimeout = setTimeout(() => {
					collapseOverflowVisible.value = true
					expandTimeout = null
				}, 350)
			} else {
				collapseOverflowVisible.value = false
			}
		}
	)

	const expandHotelFormHandler = (value: boolean) => {
		emits('change', value)
	}
	const { createForm, rulesUserValidation } = useUser({
		immediate: false,
	})

	const { createUserMutationAsync } = useCompanyUser()

	const toast = useToast()

	const {
		user,
		users,
		loading,
		availabilityForm,
		selectedBranch,
		searchSettings,
		searchCompanyId,
		loadingSearchSettings,
		searchSettingsError,
		reloadSearchSettings,
		radiusInput,
		submittingSearch,
		availabilityValidation,
		members,
		loadingMembers,
		loadingUsers,
		reservationNights,

		form,
		disclaimerIsOpen,
		actualDisclaimer,
		disclaimers,
		width,
		policyErrorFlag,
		policyInformation,
		policyErrorsRef,
		policyObjectMap,
		datePickerRef,
		loadingPolicies,
		calendarSettings,
		showingPolicies,
		availablePolicies,
		transformedAllowedCountriesPolicy,
		handleInternalDateChange,
		clearDates,
		strictPolicyBlocking,
		setUsersQuery,
		searchUsers,
		setFormValue,
		setMemberQuery,
		searchMembers,
		openDisclaimerModal,
		handleSubmit,
		searchAroundBranch,
		handleDateUpdate,
		onRemoveMember,
		onRemovePlace,
		resetGuestQuery,
		removePerson,
		resetTravelers,

		//Hotels search
		isFetching: isFetchingHotels,
		setSearchQueryHotels,
		resultsHotelsSearch,
		resultsHotelsFavorites,
		searchingHotels,

		//Google places search
		setSearchPlaceQuery,
		isFetchingSearchPlaces: isFetchingPlaces,
		transformedPlacesResults,
		transformedResultsPlaces,
		transformedResultsEstablishment,

		//Place Details
		isLoadingPlace,

		//Reservation
		createReservation,
		errorMessage,
	} = useAvailabilityForm(expandHotelFormHandler)

	const supportDock = ref<HTMLElement | null>(null)
	useInlineFloatingRail(
		supportDock,
		computed(() => props.expandHotelForm && width.value < 1024)
	)

	const nightAmountViolation = computed(() => {
		const policy = policyObjectMap.value.nightAmount
		if (!policy || !reservationNights.value) return null
		const limit = Number(policy.rule[0].value)
		// Semántica del backend/usePolicyEvaluator: nightAmount es value con lte →
		// el límite es INCLUSIVO (máx permitido = limit), viola en nights > limit.
		// Antes usaba >= limit y mostraba limit-1 (exclusivo) → una noche más estricto
		// que el enforcement real.
		if (reservationNights.value > limit) {
			return {
				limit,
				flexible: policy.isFlexible,
				displayName: policy.displayName,
				description: policy.description,
			}
		}
		return null
	})

	const applyDestinationSuggestion = () => {
		const city = localDestCity.value || destinationCity.value
		if (!city) return
		setSearchPlaceQuery(city)
	}

	const { setReservation } = useReservationStore()
	const reservationStore = useReservationStore()

	const handleSuggestFromInput = (hotelName: string) => {
		if (!maySuggestHotel.value) return
		availabilityForm.destination = {
			id: '',
			name: hotelName,
			amadeusCode: null,
		} as unknown as Suggestion
	}

	const handleUnLink = () => {
		reservationStore.resetReservation()
		const {
			linkedCefId: _lf,
			cefId: _cef,
			...rest
		} = router.currentRoute.value.query

		availabilityForm.member = undefined
		availabilityForm.travelers = []

		router.replace({ query: rest })
	}

	const { abilities, redirectTo404 } = useHasPermissionUser()

	const modalUserForm = ref(false)
	const { user: userLogged } = useAuthUser()

	// Sedes de la empresa del viajero (o del cliente a cuyo nombre busca un agente). No
	// son un destino: un clic busca hoteles en su radio guardado (searchAroundBranch).
	const {
		quickBranches,
		total: branchCount,
		suggestions: branchOptions,
		query: branchQuery,
		findBranch,
		isFetching: loadingBranches,
		isError: branchError,
		refetch: reloadBranches,
		hasNextPage: hasMoreBranches,
		fetchNextPage: moreBranches,
		isFetchingNextPage: loadingMoreBranches,
	} = useHotelBranches(
		computed(() => availabilityForm.member?.id),
		computed(() => !!userLogged.value?.internal),
		computed(() => availabilityForm.travelers.map((traveler) => traveler.id))
	)
	const branchChoice = ref<BranchSuggestion>()
	const clearSelectedBranch = () => {
		selectedBranch.value = undefined
		branchChoice.value = undefined
	}
	watch(branchChoice, (branch) => {
		if (branch) void searchFromBranch(branch)
	})

	/**
	 * La ubicación predeterminada de la empresa del viajero: su sede principal o, sin
	 * ella, la dirección de la empresa (`searchSettings.defaultLocation`, de BookingV2). Se
	 * pone **una vez por empresa**: al abrir y al cambiar de cliente o de empresa del
	 * viajero. Sólo si no hay destino, ni otra sede, ni un vuelo ligado, y no busca sola.
	 * Ningún otro vacío la vuelve a poner: «Cambiar», la barra de resumen, el mapa o
	 * «Últimas búsquedas».
	 *
	 * El alcance se anota en cuanto llegan los ajustes de esa empresa, haya o no ubicación:
	 * así, ir a una empresa sin ubicación y regresar vuelve a poner la de la primera. Al
	 * cambiar de empresa, la consulta cambia de llave y no trae datos hasta cargar, así que
	 * no se cuela la ubicación de la anterior.
	 */
	const principalScope = ref<string>()
	const automaticBranchKey = ref<string>()
	watch(
		searchSettings,
		(settings) => {
			if (!settings) return
			const scope = `${availabilityForm.member?.id ?? ''}:${searchCompanyId.value ?? ''}`
			if (principalScope.value === scope) return

			principalScope.value = scope
			const location = settings.defaultLocation
			if (
				!location ||
				selectedBranch.value ||
				availabilityForm.destination ||
				props.linkedFlight
			)
				return

			const principal = fromDefaultLocation(location)
			selectedBranch.value = principal
			automaticBranchKey.value = principal.id
		},
		{ immediate: true }
	)

	// Sin solicitante, los ajustes no se piden y no se anota otro alcance: al volver a
	// elegir al mismo, la ubicación no regresaría. Se olvida el alcance al quitarlo.
	watch(
		() => availabilityForm.member?.id,
		(id) => {
			if (!id) principalScope.value = undefined
		}
	)

	/**
	 * Escribir en «Destino» suelta la ubicación puesta sola: si no, «Buscar disponibilidad»
	 * buscaría alrededor de ella con lo escrito sin elegir. Una sede elegida a mano se queda.
	 */
	const releaseAutomaticBranch = () => {
		if (selectedBranch.value?.id === automaticBranchKey.value)
			selectedBranch.value = undefined
		automaticBranchKey.value = undefined
	}

	// Traveler "vanilla": un usuario cliente sin rol de mando (Supervisor, Travel
	// Manager o Master) solo puede reservar para SÍ MISMO — el viajero queda fijo
	// en él y no puede buscar a otros ni crear usuarios (el backend también lo valida).
	const isTravelerOnly = computed(
		() =>
			!userLogged.value?.internal &&
			!(userLogged.value?.roles ?? []).some((role) =>
				['Master', 'Travel Manager', 'Supervisor'].includes(role)
			)
	)

	watch(
		[
			isTravelerOnly,
			() => userLogged.value?.id,
			// Si cualquier flujo vacía los viajeros (des-link, resets), el Traveler
			// se re-agrega solo: sin él no hay a quién reservar y el picker está fijo.
			() => availabilityForm.travelers.length,
		],
		() => {
			if (!isTravelerOnly.value || !userLogged.value) return

			if (availabilityForm.travelers.length === 0) {
				availabilityForm.travelers = [userLogged.value]
			}
		},
		{ immediate: true }
	)

	const openModalUserCreate = () => {
		modalUserForm.value = true
	}
	const closeModalUserCreate = () => {
		modalUserForm.value = false
	}

	const v$ = useVuelidate(rulesUserValidation, createForm)

	const handleSubmitUserCreate = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		const user = await createUserMutationAsync({
			name: createForm.value.name,
			secondName: createForm.value.secondName ?? '',
			lastname: createForm.value.lastname,
			secondLastname: createForm.value.secondLastname ?? '',
			birthDate: createForm.value.birthDate,
			gender: createForm.value.gender,
			companyId:
				createForm.value.companyId ??
				(userLogged.value?.internal ?
					availabilityForm?.member?.companyId
				:	userLogged.value?.companyId),
			roleId: createForm.value.roleId ?? null,
			email: createForm.value.email,
			companyGroupId: Number(createForm.value.companyGroupId) ?? '',
			departmentId: Number(createForm.value.departmentId) ?? '',
			haveAccess: createForm.value.haveAccess,
			jobTitle: '',
			// Solo el encargado elegido en el form (validado por rol): el fallback al
			// member/usuario logueado metía TMs/Masters como supervisor de un Traveler,
			// que la jerarquía estricta ya rechaza. Sin elección, nace sin supervisor.
			supervisorId: createForm.value.supervisorId ?? null,
		})

		if (user) {
			toast.success('Usuario creado correctamente')
			closeModalUserCreate()

			availabilityForm.travelers.push(user)

			return
		}
	}

	const {
		user: memberUser,
		userId,
		refetch,
		isFetching: isFetchingMember,
	} = useUser({
		id: '',
		immediate: false,
	})

	watch(
		() => props.linkedFlight,
		(f) => {
			if (f) {
				localDestCity.value =
					f.destinationAirport?.cityName ?? f.destination ?? null
				// El vuelo llega por otra consulta: si la ubicación predeterminada se puso
				// antes, se suelta, porque con vuelo ligado el destino natural es el suyo.
				releaseAutomaticBranch()
			}
		},
		{ immediate: true }
	)

	watch(
		abilities,
		() => {
			redirectTo404('create-hotel', 'App\\Models\\Request')
		},
		{
			immediate: true,
		}
	)

	watch(
		() => props?.linkedCefRequests?.[0]?.memberId!,
		async () => {
			if (props?.linkedCefRequests?.[0]?.memberId!) {
				userId.value = props.linkedCefRequests[0].memberId
				await refetch()
				availabilityForm.member = memberUser.value
			}
		},
		{ immediate: true }
	)

	watch(
		() => props.linkedCefRequests,
		() => {
			if (props.linkedCefRequests && props.linkedCefRequests.length > 0) {
				availabilityForm.travelers =
					props.linkedCefRequests[0].traveler ?
						[props.linkedCefRequests[0].traveler]
					:	[]
			}
		},
		{ immediate: true }
	)

	const filteredUsers = computed(() => {
		return (users?.value ?? []).filter(
			(userOption) =>
				!availabilityForm.travelers.some(
					(selected) => selected.id === userOption.id
				)
		)
	})

	const maySuggestHotel = computed(() => {
		if (selectedBranch.value) {
			return (
				selectedBranch.value.hotelPreferenceMode !== undefined &&
				selectedBranch.value.hotelPreferenceMode !== 'restrict'
			)
		}
		return (
			!!searchSettings.value &&
			searchSettings.value.hotelPreferenceMode !== undefined &&
			searchSettings.value.hotelPreferenceMode !== 'restrict'
		)
	})
	const showFormRadius = computed(
		() =>
			!!searchSettings.value &&
			(searchSettings.value.allowCustomRadius ||
				searchSettings.value.blockedRadiusDisplay !== 'hidden')
	)

	const requiresManualReservation = computed(() => {
		const d = availabilityForm.destination
		if (!d) return false
		// offline: no id (suggested hotel, not in system)
		if (!d.id) return true
		// non-Amadeus hotel: it's a hotel object (has isIndependent key indicating database hotel) but amadeusCode is empty/null
		const isHotel = 'isIndependent' in d || 'isFavorite' in d
		return isHotel && !d.amadeusCode
	})

	/**
	 * «Últimas búsquedas»: vuelve a llenar destino, fechas y huéspedes por habitación con
	 * una búsqueda anterior, y también a sus viajeros si la búsqueda no terminó en reserva.
	 * En las reservadas no se tocan: esa persona ya está ocupada en esas fechas, y lo que
	 * suele seguir es buscar lo mismo para alguien más. El backend sólo manda los viajeros
	 * que quien consulta puede elegir (Master y TM su empresa, los demás su cadena).
	 *
	 * Sólo busca si se lo piden (`submit`, la acción «Buscar de nuevo» del menú): cada
	 * búsqueda es una consulta que el proveedor nos cobra. Y aun así, si falta algo
	 * —viajeros, destino que no se pudo reconstruir, fechas ya pasadas o que la política
	 * rígida del grupo ya no permite— llena lo que puede y avisa en vez de mandar una
	 * búsqueda que la validación o la política rechazarían.
	 */
	const applyRecentSearch = async (
		search: RecentHotelSearch,
		{ submit = false }: { submit?: boolean } = {}
	) => {
		const pending: string[] = []
		clearSelectedBranch()

		let branch: BranchSuggestion | undefined
		try {
			branch =
				search.companyBranchId ?
					await findBranch(search.companyBranchId)
				:	undefined
		} catch {
			toast.error(
				'No se pudo recuperar la sede de esta búsqueda. Vuelve a intentarlo.'
			)
			return
		}

		// Una búsqueda en la dirección de la empresa se guarda como un lugar sin `placeId`
		// ni sede. Se reconoce por su punto y se repite igual que entonces.
		const companyLocation = searchSettings.value?.defaultLocation
		if (
			!branch &&
			search.destinationType !== 'hotel' &&
			!search.placeId &&
			!search.companyBranchId &&
			companyLocation?.type === 'company' &&
			Math.abs(Number(search.latitude) - companyLocation.latitude) < 1e-6 &&
			Math.abs(Number(search.longitude) - companyLocation.longitude) < 1e-6
		) {
			branch = fromDefaultLocation(companyLocation)
		}

		if (branch) {
			// Búsqueda de sede o de la dirección de la empresa: se repite en su radio (su
			// punto y su radio de hoy), sin tocar «Destino». Si la sede ya no existe, cae
			// abajo al lugar que se guardó.
		} else if (
			search.destinationType === 'hotel' &&
			search.hotelId &&
			search.hotelCode
		) {
			// Misma forma que la preselección de favoritos: handleSubmit la reconoce como
			// hotel del catálogo y abre su detalle.
			availabilityForm.destination = {
				id: search.hotelId,
				name: search.destinationLabel ?? '',
				amadeusCode: search.hotelCode,
				isFavorite: false,
				latitude: search.latitude,
				longitude: search.longitude,
				stateName: search.city ?? undefined,
			} as unknown as Suggestion
		} else if (search.destinationType === 'place' && search.placeId) {
			availabilityForm.destination = {
				id: search.placeId,
				name: search.destinationLabel ?? '',
			} as unknown as Suggestion
		} else {
			// Búsquedas registradas antes de que el front mandara el place_id.
			pending.push('el destino')
		}

		setFormValue('GuestCount', Math.min(Math.max(search.guestsPerRoom, 1), 4))

		const travelers = search.booked ? [] : (search.travelers ?? [])

		if (travelers.length) {
			availabilityForm.travelers = [...travelers]

			// Alguno ya no se puede elegir (fuera de la cadena o dado de baja): con menos
			// viajeros sería otra búsqueda, así que no se lanza sola.
			if (travelers.length < search.travelersCount) {
				pending.unshift('al resto de los viajeros')
			}
		} else if (!availabilityForm.travelers.length) {
			pending.unshift('a los viajeros')
		}

		// en-CA da YYYY-MM-DD en la zona local, comparable como texto con checkIn.
		const datesPassed = search.checkIn < new Date().toLocaleDateString('en-CA')

		// Las fechas van después de los viajeros: se revisan contra la política del grupo
		// del primero, la misma que evalúa BookingV2. Con días que el calendario bloquea,
		// la búsqueda sólo regresaría el error de política. Sin viajeros no hay contra qué.
		const blockedBy =
			!datesPassed && availabilityForm.travelers.length ?
				await strictPolicyBlocking(search.checkIn, search.checkOut)
			:	undefined

		if (datesPassed || blockedBy) {
			// Vacías y no las que hubiera: el aviso pide elegirlas, y el calendario abre
			// con los días que la política deja.
			clearDates()
			pending.push(blockedBy ?? 'fechas nuevas')
		} else {
			availabilityForm.date = [search.checkIn, search.checkOut]
			handleDateUpdate([search.checkIn, search.checkOut])
		}

		await nextTick()
		if (branch) {
			selectedBranch.value = branch
			availabilityForm.destination = undefined
			// La eligió quien busca: escribir en «Destino» ya no la suelta.
			automaticBranchKey.value = undefined
		}

		const last = pending.pop()

		if (last) {
			toast.info(
				`Para buscar, elige ${pending.length ? `${pending.join(', ')} y ` : ''}${last}.`
			)
			return
		}

		if (submit) {
			await nextTick()
			await (branch ? searchAroundBranch(branch) : handleSubmit())
			return
		}

		toast.info(
			branch?.isCompanyAddress ?
				'Listo: revisa la dirección de la empresa y pulsa «Buscar disponibilidad».'
			: branch ?
				`Listo: revisa la sede «${branch.branchName}» y pulsa «Buscar disponibilidad».`
			:	'Listo: revisa los datos y busca disponibilidad.'
		)
	}

	/**
	 * Clic en una sede: busca de una vez hoteles en su radio guardado, como la tarjeta de
	 * «Últimas búsquedas». No usa «Destino»: la sede no es un lugar de Google ni un hotel.
	 * Si faltan viajeros o fechas avisa en vez de buscar (cada búsqueda la cobra el
	 * proveedor).
	 */
	const searchFromBranch = async (branch: BranchSuggestion) => {
		if (submittingSearch.value || searchingHotels.value) return
		// Elegida a mano: escribir en «Destino» ya no la suelta.
		automaticBranchKey.value = undefined
		const pending = await searchAroundBranch(branch)

		if (pending.length) {
			toast.info(
				`Para buscar cerca de ${branch.branchName}, elige ${pending.join(' y ')}.`
			)
		}
	}

	defineExpose({
		availabilityForm,
		availabilityValidation,
		loading,
		createReservation,
		policyObjectMap,
		clearBranch: clearSelectedBranch,
		applyRecentSearch,
	})
</script>

<template>
	<Collapse
		:when="expandHotelForm"
		class="form-collapse relative z-20"
		:class="{ '!overflow-visible': collapseOverflowVisible }"
	>
		<form
			@submit.prevent="handleSubmit"
			autocomplete="off"
		>
			<div class="bg-white border-b border-gray-200 shadow-sm">
				<!-- Form body -->
				<div class="mx-auto max-w-4xl px-4 sm:px-6 py-6 flex flex-col gap-5">
					<!-- Linked flight banner -->
					<Transition
						enter-active-class="transition-all duration-300 ease-out"
						enter-from-class="opacity-0 -translate-y-2"
						enter-to-class="opacity-100 translate-y-0"
						leave-active-class="transition-all duration-200 ease-in"
						leave-from-class="opacity-100 translate-y-0"
						leave-to-class="opacity-0 -translate-y-2"
					>
						<LinkedFlightBanner
							v-if="router.currentRoute.value.query.linkedCefId"
							:linked-service="linkedCefRequests"
							:linked-flight="linkedFlight ?? null"
							service-type="Hotel"
							:member="availabilityForm.member"
							:travelers="availabilityForm.travelers"
							:loading-member="isFetchingMember"
							@unlink="handleUnLink"
						/>
					</Transition>

					<!-- Row 1: Member (internal only) -->
					<StyledCombobox
						v-if="user?.internal"
						v-model="availabilityForm.member"
						is-required
						label="Solicitado por"
						placeholder="Buscar"
						:options="members"
						:loading-options="loadingMembers"
						:show-input="!availabilityForm.member"
						:input-overlap-tooltip="
							availabilityForm.member &&
							`${availabilityForm.member.name} ${availabilityForm.member.lastname} (${availabilityForm.member.email})`
						"
						:error="availabilityValidation.member.$errors.at(0)?.$message ?? ''"
						:on-input-change="
							(value: string) => {
								setMemberQuery(value)
								if (value.length) searchMembers()
							}
						"
					>
						<template #default="{ option }">
							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<span class="text-xs font-medium"
										>{{ option.name }} {{ option.lastname }}</span
									>
									<small class="text-xs text-gray-500">{{
										option.email || null
									}}</small>
								</div>
								<small class="text-[10px] text-gray-400">{{
									option.companyName || 'Sin empresa'
								}}</small>
							</div>
						</template>
						<template #input-overlap>
							<template v-if="availabilityForm.member">
								<p class="truncate text-sm normal-case md:text-base">
									{{
										`${availabilityForm.member.name} ${availabilityForm.member.lastname} (${availabilityForm.member.email})`
									}}
								</p>
								<button
									class="material-symbols-outlined text-[22px]"
									@click.prevent="
										() => {
											!router.currentRoute.value.query.linkedFlightId ?
												onRemoveMember()
											:	handleUnlinkAlert()
										}
									"
								>
									close
								</button>
							</template>
						</template>
					</StyledCombobox>

					<!-- Row 2: Travelers + create user -->
					<div class="flex items-end gap-3">
						<StyledCombobox
							v-model="availabilityForm.travelers"
							multiple
							is-required
							class="w-full"
							placeholder="Buscar viajeros"
							:options="filteredUsers"
							:loading-options="loadingUsers"
							:limit="6"
							:disabled="
								isTravelerOnly || (user?.internal && !availabilityForm.member)
							"
							:error="
								availabilityValidation.travelers.$errors.at(0)?.$message ?? ''
							"
							@blur="() => searchUsers()"
							:input-tooltip="
								isTravelerOnly ?
									'Como Traveler solo puedes reservar para ti mismo'
								: user?.internal && !availabilityForm.member ?
									'Debe seleccionar un socio para poder buscar viajeros'
								:	''
							"
							:on-input-change="
								(value: string) => {
									setUsersQuery(value)
									searchUsers()
								}
							"
						>
							<template #label="{ required, loading }">
								<TravelersCustomLabel
									:user="user"
									:required="required"
									:loading="!!loading"
									:disclaimers="disclaimers"
									:availability-form="availabilityForm"
									:available-policies="availablePolicies"
									:locked-to-self="isTravelerOnly"
									@show-policies-modal="showingPolicies = true"
									@openDisclaimerModal="openDisclaimerModal"
									@reset-guest-query="resetGuestQuery"
									@remove-person="
										(id: number) => {
											!router.currentRoute.value.query.linkedFlightId ?
												removePerson(id)
											:	handleUnLink()
										}
									"
									@reset-travelers="resetTravelers"
								>
									<template #policies>
										<StyledPoliciesModal
											v-if="availablePolicies"
											:policies="{
												...policyObjectMap,
												allowedCountries:
													policyObjectMap.allowedCountries &&
													(transformedAllowedCountriesPolicy ?? null),
											}"
										/>
									</template>
								</TravelersCustomLabel>
							</template>
							<template #default="{ option }">
								<div class="flex flex-col">
									<div class="flex items-center justify-between">
										<span class="text-xs font-medium"
											>{{ option.name }} {{ option.lastname }}</span
										>
										<small class="text-xs text-gray-500">{{
											option.email || null
										}}</small>
									</div>
									<small class="text-[10px] text-gray-400">{{
										option.companyName || 'Sin empresa'
									}}</small>
								</div>
							</template>
						</StyledCombobox>

						<HasPermission
							v-if="!isTravelerOnly"
							name-permission="create-users"
							:entity-type="'App\\Models\\Company'"
						>
							<StyledButton
								:class="[
									'material-symbols-outlined text-white transition-colors',
									user?.internal && !availabilityForm.member ?
										'cursor-not-allowed bg-green-300'
									:	'cursor-pointer bg-green-500 hover:bg-green-600',
								]"
								type="button"
								@click="openModalUserCreate"
								:disabled="
									user?.internal && !availabilityForm.member?.companyId
								"
								v-tooltip="
									user?.internal ?
										`${!availabilityForm.member ? 'Selecciona un socio para crear un viajero' : `Crea un viajero con la empresa ${availabilityForm.member.companyName}`}`
									:	'Crea un viajero'
								"
								>group_add</StyledButton
							>
						</HasPermission>

						<ModalComponent
							:model-value="modalUserForm"
							@update:model-value="closeModalUserCreate()"
						>
							<template #modalBody>
								<UserForm
									@cancel-form="closeModalUserCreate"
									@send-form="handleSubmitUserCreate"
									:company-selected="availabilityForm.member?.companyId"
									:group-selected="
										availabilityForm.travelers[0]?.companyGroupId
									"
									:corporation-user="availabilityForm.member?.corporationId"
									:default-supervisor="
										userLogged?.internal ? availabilityForm.member : userLogged
									"
								/>
							</template>
						</ModalComponent>
					</div>

					<!-- Row 3: Destination + Dates + ECR -->
					<div class="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
						<!-- Destination suggestion chip -->
						<Transition
							enter-active-class="transition-all duration-200 ease-out"
							enter-from-class="opacity-0 -translate-y-1"
							enter-to-class="opacity-100 translate-y-0"
						>
							<div
								v-if="linkedFlight"
								class="md:col-span-2 -mb-1"
							>
								<button
									type="button"
									class="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-[#021F58] hover:bg-blue-100 transition-colors"
									@click="applyDestinationSuggestion"
								>
									<span class="material-symbols-outlined text-[13px]"
										>place</span
									>
									Buscar en
									<strong>{{ localDestCity || destinationCity }}</strong>
									<span class="material-symbols-outlined text-[13px]"
										>arrow_forward</span
									>
								</button>
							</div>
						</Transition>

						<StyledComboboxGroup
							label="Destino o nombre del hotel"
							placeholder="Selecciona destino o nombre del hotel"
							v-model="availabilityForm.destination"
							:disabled="!availabilityForm.travelers.length || loadingPolicies"
							:options="[
								resultsHotelsFavorites || [],
								transformedPlacesResults,
								transformedResultsPlaces,
								transformedResultsEstablishment,
								resultsHotelsSearch || [],
							]"
							:label-options="[
								`${user?.internal ? `Favoritos de ${availabilityForm.member?.fullName}` : 'Mis hoteles favoritos'}`,
								'Region o ciudad',
								'Aeropuertos',
								'Lugares de interés',
								'Hoteles',
							]"
							:label-icons="[
								'favorite',
								'place',
								'flight',
								'attractions',
								'apartment',
							]"
							:on-input-change="
								(value: string) => {
									if (value.trim()) releaseAutomaticBranch()
									setSearchPlaceQuery(value)
									setSearchQueryHotels(value)
								}
							"
							:show-input="!availabilityForm.destination"
							:loading-options="isFetchingHotels || isFetchingPlaces"
							:is-required="true"
							:error="
								selectedBranch ? '' : (
									(availabilityValidation.destination.$errors.at(0)?.$message ??
									'')
								)
							"
							:reset-query-after-select="true"
							:debounce-delay="500"
						>
							<template #input-overlap>
								<template v-if="availabilityForm.destination">
									<p class="text-sm md:text-base">
										{{ availabilityForm.destination.name }}
									</p>
									<button
										class="material-symbols-outlined text-[22px]"
										@click.prevent="onRemovePlace()"
									>
										close
									</button>
								</template>
							</template>
							<template #no-results="{ query }">
								<div class="flex flex-col gap-2 p-2 text-left">
									<p class="text-xs font-medium text-gray-400">
										Sin resultados para "{{ query }}"
									</p>
									<p
										v-if="!maySuggestHotel"
										class="text-xs text-gray-600"
									>
										Tu empresa limita la búsqueda a sus hoteles configurados.
										Consulta al administrador si necesitas otra opción.
									</p>
									<button
										v-if="maySuggestHotel"
										type="button"
										class="flex w-full items-center gap-1 border-t border-gray-100 pt-2 text-left text-xs font-semibold text-[#253C70] hover:text-[#1a2b52]"
										@mousedown="handleSuggestFromInput(query)"
									>
										<span class="material-symbols-outlined text-[15px]"
											>edit_note</span
										>
										¿No encuentras el hotel que buscas? Sugiérelo aquí
									</button>
								</div>
							</template>

							<template #after-options="{ query }">
								<div
									v-if="query && maySuggestHotel"
									class="p-2 text-left"
								>
									<button
										type="button"
										class="flex w-full items-center gap-1 text-left text-xs font-semibold text-[#253C70] hover:text-[#1a2b52]"
										@mousedown="handleSuggestFromInput(query)"
									>
										<span class="material-symbols-outlined text-[15px]"
											>edit_note</span
										>
										¿No encuentras el hotel que buscas? Sugiérelo aquí
									</button>
								</div>
							</template>
						</StyledComboboxGroup>

						<StyledDateInput
							ref="datePickerRef"
							:disabled="!availabilityForm.travelers.length || loadingPolicies"
							is-required
							range
							auto-apply
							arrow-navigation
							:clear-on-open="!!policyObjectMap.nightAmount"
							label="Fechas"
							placeholder="Entrada - Salida"
							v-model="availabilityForm.date"
							:multi-calendars="width > 768"
							model-type="yyyy-MM-dd"
							format="dd/MM/yyyy"
							preview-format="dd/MM/yyyy"
							:enable-time-picker="false"
							:position="'center'"
							:min-date="calendarSettings.minDate"
							:max-date="calendarSettings.maxDate"
							:max-range="calendarSettings.maxRange"
							:allowed-dates="calendarSettings.allowedDates"
							:prevent-min-max-navigation="true"
							:error="availabilityValidation.date.$errors.at(0)?.$message ?? ''"
							:handle-internal="handleInternalDateChange"
							@update:model-value="handleDateUpdate"
						/>

						<!-- ECR booking (internal) -->
						<div
							v-if="user?.internal"
							class="md:col-span-2"
							:class="{ hidden: requiresManualReservation }"
						>
							<div class="flex items-center gap-2 mb-1">
								<span class="text-sm font-medium text-gray-700"
									>ECR booking</span
								>
								<span
									:class="[
										'w-fit cursor-pointer rounded-md p-0.5 transition-colors',
										availabilityForm.travelers.length > 1 ?
											'bg-amber-50'
										:	'bg-gray-100',
									]"
									@click.prevent="openDisclaimerModal(disclaimers.ecr)"
								>
									<QuestionMarkCircleIcon
										:class="[
											'h-4 w-4 transition-colors',
											availabilityForm.travelers.length > 1 ?
												'text-yellow-400'
											:	'text-gray-400',
										]"
										aria-hidden="true"
									/>
								</span>
								<LoadingSpin
									v-if="loading"
									class="!h-4 !w-4"
									dark
								/>
							</div>
							<StyledInput
								v-model="availabilityForm.previousRequest"
								:disabled="
									!availabilityForm.travelers.length ||
									loadingPolicies ||
									availabilityForm.travelers.length >= 2
								"
								:class="{
									'cursor-not-allowed': !availabilityForm.travelers.length,
								}"
							/>
						</div>
					</div>

					<section
						v-if="branchCount || branchError || selectedBranch"
						class="min-w-0 space-y-3"
						:aria-label="
							branchCount || branchError ?
								'Buscar cerca de tus sedes'
							:	'Dónde abre el buscador'
						"
					>
						<!--
							Sin sedes, la sección sólo enseña la ubicación elegida (la dirección de la
							empresa): si no se viera, «Buscar» buscaría ahí sin avisar.
						-->
						<p
							v-if="branchCount || branchError"
							class="text-xs font-medium text-gray-600"
						>
							Buscar cerca de tus sedes
							<span v-if="branchCount">({{ branchCount }})</span>
						</p>
						<div
							v-if="branchCount"
							class="flex flex-wrap gap-2"
						>
							<button
								v-for="branch in quickBranches"
								:key="branch.id"
								type="button"
								:disabled="
									loadingPolicies || submittingSearch || searchingHotels
								"
								:title="branch.name"
								class="inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium disabled:opacity-50"
								:class="
									selectedBranch?.branchId === branch.branchId ?
										'border-[#253C70] bg-[#253C70] text-white'
									:	'border-gray-200 bg-white text-[#253C70] hover:bg-gray-50'
								"
								@click="searchFromBranch(branch)"
							>
								<span
									class="material-symbols-outlined text-[16px]"
									aria-hidden="true"
									>apartment</span
								>
								<span class="truncate">{{ branch.branchName }}</span
								><span
									v-if="branch.isDefault"
									class="shrink-0 font-normal opacity-80"
									>· principal</span
								><span
									v-if="showFormRadius"
									class="shrink-0"
									>·
									{{
										branch.effectiveRadiusKm ??
										branch.radiusKm ??
										searchSettings?.defaultRadiusKm
									}}
									km</span
								>
							</button>
						</div>
						<template v-if="branchCount > 3">
							<StyledCombobox
								v-model="branchChoice"
								label="Buscar otra sede"
								placeholder="Nombre o dirección de la sede"
								:options="branchOptions"
								:loading-options="loadingBranches"
								:disabled="submittingSearch || searchingHotels"
								:debounce-delay="300"
								:hide-options-after-select="true"
								:prevent-auto-focus="true"
								:on-input-change="(value: string) => (branchQuery = value)"
							>
								<template #default="{ option }"
									><span class="block whitespace-normal break-words text-sm">{{
										option.branchName
									}}</span
									><span class="block truncate text-xs opacity-70"
										>{{ option.name
										}}<template v-if="showFormRadius">
											·
											{{
												option.effectiveRadiusKm ??
												option.radiusKm ??
												searchSettings?.defaultRadiusKm
											}}
											km</template
										></span
									></template
								>
							</StyledCombobox>
							<button
								v-if="hasMoreBranches"
								type="button"
								:disabled="loadingMoreBranches"
								class="rounded px-2 py-1 text-xs font-medium text-[#253C70] underline disabled:opacity-50"
								@click="moreBranches()"
							>
								{{
									loadingMoreBranches ? 'Cargando sedes…' : (
										'Cargar más sedes de esta búsqueda'
									)
								}}
							</button>
						</template>
						<p
							v-if="branchError"
							role="alert"
							class="text-xs text-red-700"
						>
							No se pudieron cargar las sedes.
							<button
								type="button"
								class="underline"
								@click="reloadBranches()"
							>
								Reintentar
							</button>
						</p>
						<div
							v-if="selectedBranch"
							class="flex min-w-0 items-start justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-[#253C70]"
							aria-live="polite"
						>
							<p class="min-w-0 break-words">
								Buscarás cerca de
								<strong>{{ selectedBranch.branchName }}</strong
								><template v-if="selectedBranch.isCompanyAddress">
									(dirección de la empresa<template
										v-if="selectedBranch.address"
										>: {{ selectedBranch.address }}</template
									>)</template
								><template v-else-if="selectedBranch.isDefault">
									(sede principal)</template
								><template v-if="showFormRadius"
									>, a
									{{
										(
											selectedBranch.isCompanyAddress &&
											searchSettings?.allowCustomRadius
										) ?
											radiusInput
										:	(selectedBranch.effectiveRadiusKm ??
											selectedBranch.radiusKm ??
											searchSettings?.defaultRadiusKm)
									}}
									km</template
								>.
								{{
									!selectedBranch.isCompanyAddress ?
										'Se aplican los hoteles configurados para esta sede.'
									: selectedBranch.hotelPreferenceMode ?
										'Se aplican los hoteles preferidos de la empresa.'
									:	''
								}}
							</p>
							<button
								type="button"
								class="shrink-0 rounded p-1 underline"
								:disabled="submittingSearch"
								@click="clearSelectedBranch"
							>
								Cambiar
							</button>
						</div>
					</section>

					<div
						v-if="
							(!selectedBranch || selectedBranch.isCompanyAddress) &&
							!requiresManualReservation &&
							((loadingSearchSettings && !searchSettings) ||
								searchSettingsError ||
								showFormRadius)
						"
						class="w-full min-w-0"
						:class="
							searchSettings?.allowCustomRadius && !searchSettingsError ?
								'rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'
							:	''
						"
						role="region"
						aria-label="Radio de búsqueda"
					>
						<p
							v-if="loadingSearchSettings && !searchSettings"
							role="status"
							class="text-xs text-gray-500"
						>
							Cargando configuración de búsqueda…
						</p>
						<p
							v-else-if="searchSettingsError"
							role="alert"
							class="text-xs text-gray-600"
						>
							Se aplicará el radio configurado por tu empresa o agencia.
							<button
								type="button"
								class="underline"
								@click="reloadSearchSettings()"
							>
								Recargar configuración
							</button>
						</p>
						<template v-else-if="searchSettings && showFormRadius">
							<template v-if="searchSettings.allowCustomRadius">
								<SearchRadiusInput
									id="hotel-search-radius"
									v-model="radiusInput"
									compact
									:disabled="submittingSearch || searchingHotels"
								/>
								<div
									class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500"
								>
									<span
										>Recomendado Enlaceforte:
										{{ searchSettings.providerRadiusKm }} km</span
									>
									<button
										v-if="radiusInput !== searchSettings.providerRadiusKm"
										type="button"
										class="min-h-8 rounded px-1 font-medium text-[#253C70] underline"
										:disabled="submittingSearch || searchingHotels"
										@click="radiusInput = searchSettings.providerRadiusKm"
									>
										Usar recomendado
									</button>
									<span
										v-if="searchSettings.companyRadiusKm !== null"
										v-tooltip="
											'Puedes ajustar el radio para esta búsqueda. Al elegir una sede se respeta su configuración.'
										"
									>
										Predeterminado de tu empresa:
										{{ searchSettings.companyRadiusKm }} km
									</span>
								</div>
							</template>
							<p
								v-else
								class="text-sm text-gray-700"
							>
								Radio de búsqueda: {{ searchSettings.defaultRadiusKm }} km
							</p>
						</template>
					</div>

					<!-- Row 4: Guests counter + summary -->
					<div class="flex flex-wrap items-center gap-6">
						<div class="flex flex-col gap-1.5">
							<div class="flex items-center gap-1.5">
								<label class="text-sm font-medium text-gray-700"
									>Huéspedes por habitación</label
								>
								<span
									:class="[
										'cursor-pointer rounded-md p-0.5 transition-colors',
										form.GuestCount > 1 && form.Quantity > 1 ?
											'bg-amber-50'
										:	'bg-gray-100',
									]"
									@click.prevent="openDisclaimerModal(disclaimers.rooms)"
								>
									<QuestionMarkCircleIcon
										:class="[
											'h-4 w-4 transition-colors',
											form.GuestCount > 1 && form.Quantity > 1 ?
												'text-yellow-400'
											:	'text-gray-400',
										]"
										aria-hidden="true"
									/>
								</span>
							</div>
							<div
								class="flex items-center rounded-lg border border-gray-200 bg-gray-50 w-fit overflow-hidden"
							>
								<button
									type="button"
									class="flex items-center justify-center w-8 h-8 text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
									:disabled="form.GuestCount <= 1"
									@click="
										form.GuestCount != 1 &&
										setFormValue('GuestCount', form.GuestCount - 1)
									"
								>
									<MinusIcon class="h-4 w-4" />
								</button>
								<span
									class="w-8 text-center text-sm font-semibold text-gray-800"
									>{{ form.GuestCount }}</span
								>
								<button
									type="button"
									class="flex items-center justify-center w-8 h-8 text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
									:disabled="form.GuestCount >= 4"
									@click="
										form.GuestCount != 4 &&
										setFormValue('GuestCount', form.GuestCount + 1)
									"
								>
									<PlusIcon class="h-4 w-4" />
								</button>
							</div>
						</div>

						<div
							v-if="availabilityForm.travelers.length > 0"
							class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
						>
							<span class="material-symbols-outlined text-[16px] text-[#021F58]"
								>hotel</span
							>
							<span class="text-sm font-medium text-gray-700">
								{{ availabilityForm.travelers.length }}
								{{
									availabilityForm.travelers.length === 1 ?
										'habitación'
									:	'habitaciones'
								}}
							</span>
							<template
								v-if="
									Array.isArray(availabilityForm.date) &&
									availabilityForm.date.length > 0
								"
							>
								<span class="text-gray-300">·</span>
								<span
									class="material-symbols-outlined text-[16px] text-[#021F58]"
									>bedtime</span
								>
								<span class="text-sm font-medium text-gray-700">
									{{ reservationNights }}
									{{ reservationNights > 1 ? 'noches' : 'noche' }}
								</span>
							</template>
						</div>
					</div>

					<!-- Night amount policy alert -->
					<div
						v-if="nightAmountViolation"
						class="flex items-start gap-3 p-3 sm:p-4 rounded-xl border"
						:class="
							nightAmountViolation.flexible ?
								'bg-amber-50 border-amber-200'
							:	'bg-red-50 border-red-200'
						"
					>
						<span
							class="material-symbols-outlined flex-shrink-0 mt-0.5 text-base"
							:class="
								nightAmountViolation.flexible ? 'text-amber-500' : (
									'text-red-500'
								)
							"
							>{{ nightAmountViolation.flexible ? 'warning' : 'error' }}</span
						>
						<div class="flex-1">
							<div class="flex items-center gap-1.5">
								<p
									class="text-sm font-semibold"
									:class="
										nightAmountViolation.flexible ? 'text-amber-800' : (
											'text-red-800'
										)
									"
								>
									{{
										nightAmountViolation.flexible ? 'Alerta de política' : (
											'Política excedida'
										)
									}}
								</p>
								<span
									class="material-symbols-outlined text-sm cursor-help text-gray-400"
									v-tooltip="
										`${nightAmountViolation.displayName} — Límite: ${nightAmountViolation.limit} noches\n${nightAmountViolation.description}\n${nightAmountViolation.flexible ? '✓ Política flexible: la reservación puede continuar pero se notificará al superior.' : '✗ Política estricta: no es posible continuar con esta reservación.'}`
									"
									>info</span
								>
							</div>
							<p
								class="text-xs mt-0.5"
								:class="
									nightAmountViolation.flexible ? 'text-amber-700' : (
										'text-red-700'
									)
								"
							>
								{{ reservationNights }} noches supera el máximo permitido de
								{{ nightAmountViolation.limit }}
								{{ nightAmountViolation.limit === 1 ? 'noche' : 'noches' }}.
								{{
									nightAmountViolation.flexible ?
										'Se notificará a su superior.'
									:	'Ajuste las fechas para continuar.'
								}}
							</p>
						</div>
					</div>

					<!-- Policy errors -->
					<div ref="policyErrorsRef">
						<PolicyErrors
							v-if="policyErrorFlag && policyInformation?.errors"
							:policyInformation="policyInformation"
						/>
					</div>

					<!-- Disclaimer alert -->
					<AlertComponent
						:dismiss="true"
						v-model="disclaimerIsOpen"
						type="warning"
					>
						<template #title>Aviso</template>
						<template #body>
							<p class="text-justify text-sm">
								{{ actualDisclaimer }}<span class="text-red-600">*</span>
							</p>
						</template>
						<template #closeButton>Entendido</template>
					</AlertComponent>
				</div>

				<!-- Alert hotel independiente -->
				<div
					v-if="requiresManualReservation && maySuggestHotel"
					class="mx-auto max-w-4xl px-4 sm:px-6 pb-4"
				>
					<StyledAlert
						:hide-nav="true"
						:title="
							user?.internal ?
								'Aviso hotel fuera de línea'
							:	'Aviso hotel independiente'
						"
						:footer-message="
							user?.internal ?
								'El hotel seleccionado no se encuentra en amadeus, se debe proceder a crear una reserva manualmente con los datos de la reservacion ya confirmada por el hotel.'
							:	'Este hotel esta fuera de línea, es necesario generar una solicitud para el área de reservaciones de enlaceforte, se le contactara con la confirmación del hotel una vez enviada la solicitud.'
						"
						type="Warning"
						title-style="text-sm md:text-base"
						:class-styles="'w-full p-3 rounded-lg border bg-amber-50 border-amber-200'"
					/>
				</div>

				<!-- Footer actions -->
				<div class="border-t border-gray-100 bg-gray-50/80 px-4 sm:px-6 py-3">
					<div class="mx-auto max-w-4xl flex items-center justify-between">
						<HasPermission
							v-if="requiresManualReservation && maySuggestHotel"
							name-permission="create-hotel"
							:entity-type="'App\\Models\\Request'"
						>
							<button
								type="button"
								class="inline-flex items-center gap-2 rounded-lg bg-[#021F58] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#1C3F7A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#021F58]/30"
								@click="createReservation"
							>
								{{
									user?.internal ? 'Crear reserva manual' : 'Llenar solicitud'
								}}
							</button>
						</HasPermission>

						<template v-else>
							<HasPermission
								name-permission="create-hotel"
								:entity-type="'App\\Models\\Request'"
							>
								<button
									type="submit"
									:disabled="
										isLoadingPlace || searchingHotels || submittingSearch
									"
									:class="[
										'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#021F58]/30',
										isLoadingPlace || searchingHotels || submittingSearch ?
											'cursor-not-allowed bg-[#021F58]/50'
										:	'bg-[#021F58] hover:bg-[#1C3F7A]',
									]"
								>
									<LoadingSpin
										v-if="isLoadingPlace || searchingHotels || submittingSearch"
										class="!h-4 !w-4"
									/>
									{{
										isLoadingPlace || searchingHotels || submittingSearch ?
											'Buscando...'
										:	'Buscar disponibilidad'
									}}
								</button>
							</HasPermission>

							<button
								type="button"
								class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
								:disabled="loading"
								@click.prevent="expandHotelFormHandler(false)"
							>
								<span class="material-symbols-outlined text-[20px]"
									>keyboard_arrow_up</span
								>
							</button>
						</template>
					</div>
				</div>
			</div>
			<div
				ref="supportDock"
				class="mx-auto flex min-h-[68px] max-w-4xl items-center justify-between gap-3 px-4 py-2 lg:hidden"
				aria-label="Ayuda para tu búsqueda"
			>
				<span class="text-xs text-gray-500">¿Necesitas ayuda?</span>
			</div>
		</form>
	</Collapse>

	<UnlinkItineraryWarningModal
		v-model="showUnlinkAlert"
		:linked-requests="linkedCefRequests ?? []"
		@confirm="
			() => {
				handleUnLink()
				showUnlinkAlert = false
			}
		"
		@cancel="showUnlinkAlert = false"
	/>
</template>
